import type { AxiosInstance } from 'axios';
import type {
  PageViewInput,
  PageViewsAggregateQuery,
  PageViewsAggregateResponse,
  UiEventInput,
  UiEventsAggregateQuery,
  UiEventsAggregateResponse,
} from '@commerce-os/shared-types';

/**
 * Phase 34 Session 34.3 — Telemetry ingest wrappers.
 *
 * Both endpoints accept a batch payload and return 204. The dashboard's
 * TelemetryClient consumer wraps these inside its `transport` callback;
 * the POS POST wraps them with a fetch-based fallback that respects the
 * offline AsyncStorage queue.
 */

export function sendPageViews(client: AxiosInstance, events: PageViewInput[]): Promise<void> {
  return client.post('/telemetry/page-view', { events }).then(() => undefined);
}

export function sendUiEvents(client: AxiosInstance, events: UiEventInput[]): Promise<void> {
  return client.post('/telemetry/ui-event', { events }).then(() => undefined);
}

// ── Phase 34 Session 34.4 — read aggregates ─────────────────────────────────

/**
 * Aggregate page-view rows for the caller's org. Group-by axis defaults
 * to `'route'` server-side; `from` / `to` are optional ISO 8601 bounds.
 * Server enforces `auth.view_usage_analytics` permission.
 */
export function getPageViewsAggregate(
  client: AxiosInstance,
  query: PageViewsAggregateQuery = {},
): Promise<PageViewsAggregateResponse> {
  return client
    .get<PageViewsAggregateResponse>('/telemetry/page-views', { params: query })
    .then((res) => res.data);
}

/**
 * Aggregate UI-event rows. Default group-by `'event'`; optional surface
 * filter narrows further. Same permission gate as page-views read.
 */
export function getUiEventsAggregate(
  client: AxiosInstance,
  query: UiEventsAggregateQuery = {},
): Promise<UiEventsAggregateResponse> {
  return client
    .get<UiEventsAggregateResponse>('/telemetry/ui-events', { params: query })
    .then((res) => res.data);
}
